// State and transport for a workspace chat (Lane 1355, moved from the sourcing
// event chat of Lane 1322 slice D without a change in behavior). The server owns
// the conversation (it mints the id and binds it to the workspace) and every
// figure; this hook only sends messages, shows the live draft, and tells the
// workspace to reload.
//
// `transport` (memoize it in the caller): load() reads the thread, create()
// mints the conversation for the first message, send(body, onEvent) streams one
// turn. There is no "New conversation" control (Ryan, Lane 1372): a workspace keeps
// one thread. Optional
// capture(clientTurnId): called at once when a message is sent or retried, before
// any wait, so a workspace can pin what the buyer was looking at (the deck chat).
// `chatSurface` (quoting, sourcing, deck): each answered turn goes to the admin
// chat log under that name (utils/chatLog.js, Lane 1391).
// `reloadForCard(card)`: whether a card that arrives mid-turn reloads the workspace.
// `focusFromCards(cards)`: what the workspace should show after a finished turn.
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import AuthContext from '../../AuthContext';
import { logChatTurn } from '../../utils/chatLog';
import { mirrorStreamReduce } from '../../utils/mirrorAnswerStream';
import { chatErrorMessage } from '../../utils/chatErrorMessage';
import { newKey } from '../../utils/clientKey';

function turnError(result) {
  const detail = result && result.errData && result.errData.detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return chatErrorMessage({ status: result ? result.status : 0 }, result ? result.errData : null);
}

export default function useWorkspaceChat({ transport, onChanged, onFocus, reloadForCard, focusFromCards,
  chatSurface }) {
  // Read without useAuth: a workspace rendered outside the provider logs nothing.
  const authRef = useRef(null);
  authRef.current = useContext(AuthContext);
  const [available, setAvailable] = useState(null);   // null while loading
  const [conversationId, setConversationId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState(null);
  const [progress, setProgress] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const focusRef = useRef(onFocus);
  focusRef.current = onFocus;
  const reloadForCardRef = useRef(reloadForCard);
  reloadForCardRef.current = reloadForCard;
  const focusFromCardsRef = useRef(focusFromCards);
  focusFromCardsRef.current = focusFromCards;
  // Each chat proposal's state from the server: active, consumed or replaced.
  const [proposals, setProposals] = useState({});
  const liveRef = useRef(true);
  // One turn at a time, decided synchronously: a second quick send must not
  // start (and must not mint a second conversation) before state updates.
  const busyRef = useRef(false);

  // A turn is saved only when its answer is finished, so a screen that loads while
  // one is still being answered (a reload, another tab) says so and looks again.
  const [answering, setAnswering] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  // Resolves to the number of turns still being answered, or null when the read failed.
  const fetchThread = useCallback(() => transport.load().then((r) => {
    if (!liveRef.current || busyRef.current) return null;
    setAvailable(Boolean(r.available));
    setConversationId(r.conversation_id || null);
    setTurns(r.turns || []);
    setProposals(r.proposals || {});
    const running = r.running_turns || 0;
    setAnswering(running > 0);
    return running;
  }), [transport]);

  useEffect(() => {
    liveRef.current = true;
    fetchThread().catch((e) => { if (liveRef.current) { setAvailable(false); setLoadError(e.message); } });
    return () => { liveRef.current = false; };
  }, [fetchThread]);

  // While an answer is being written elsewhere, look again every 3 seconds. A failed
  // look keeps the thread as it is and tries again (it never switches the chat off);
  // the workspace reloads once, when the answer lands, not on every look.
  useEffect(() => {
    if (!answering) return undefined;
    const t = setTimeout(() => {
      fetchThread()
        .then((running) => {
          if (running === 0 && changedRef.current) changedRef.current();
          else if (running === null) setPollTick((n) => n + 1);
        })
        .catch(() => { if (liveRef.current) setPollTick((n) => n + 1); });
    }, 3000);
    return () => clearTimeout(t);
  }, [answering, turns, pollTick, fetchThread]);

  const run = useCallback(async (question, clientTurnId, existingConversation) => {
    if (transport.capture) transport.capture(clientTurnId);
    setBusy(true);
    // The turn is on screen before anything can fail, so a failed first message keeps
    // its text and its Try again (Codex UI slice 1 round 3).
    setDraft(null);
    setProgress('Looking into your question…');
    setTurns((prev) => [...prev.filter((t) => t.client_turn_id !== clientTurnId),
      { client_turn_id: clientTurnId, question, answer: '', event_cards: [], pending: true }]);
    let conv = existingConversation;
    if (!conv) {
      try {
        const made = await transport.create();
        conv = made.conversation_id;
        if (liveRef.current) setConversationId(conv);
      } catch (e) {
        busyRef.current = false;
        if (!liveRef.current) return;
        setBusy(false);
        setProgress('');
        setTurns((prev) => prev.map((t) => (t.client_turn_id === clientTurnId
          ? { ...t, pending: false, error: e.message || 'The chat could not start. Try again.' } : t)));
        return;
      }
    }
    let result;
    try {
      result = await transport.send(
        { conversation_id: conv, client_turn_id: clientTurnId, query: question },
        (event) => {
          if (!liveRef.current) return;
          if (event.type === 'stage' && event.line) setProgress(event.line);
          else if (event.type === 'answer_delta' || event.type === 'answer_reset') {
            setDraft((prev) => mirrorStreamReduce(prev, event));
          } else if (event.type === 'card' && event.card) {
            setTurns((prev) => prev.map((t) => (t.client_turn_id === clientTurnId
              ? { ...t, event_cards: [...t.event_cards, event.card] } : t)));
            if (reloadForCardRef.current && reloadForCardRef.current(event.card)
              && changedRef.current) changedRef.current();
          }
        });
    } catch (e) {
      // The connection dropped on both paths. The turn keeps its id, so its
      // Try again replays it (or answers from its saved changes) instead of
      // a new message that could repeat a change.
      result = { ok: false, status: 0,
        errData: { detail: { message: 'The connection dropped. Try again.' } } };
    }
    busyRef.current = false;
    if (!liveRef.current) return;
    setBusy(false);
    setDraft(null);
    setProgress('');
    if (result.ok && result.data) {
      const data = result.data;
      logChatTurn(authRef.current, chatSurface, { question, answer: String(data.answer || ''),
        conversationId: conv, clientTurnId });
      setTurns((prev) => prev.map((t) => (t.client_turn_id === clientTurnId
        ? { client_turn_id: clientTurnId, question, answer: String(data.answer || ''),
          event_cards: data.event_cards || [] } : t)));
      // A new proposal replaces the one waiting; a yes consumes it: read their states.
      transport.load().then((r) => { if (liveRef.current) setProposals(r.proposals || {}); })
        .catch(() => {});
      const focus = focusFromCardsRef.current ? focusFromCardsRef.current(data.event_cards) : null;
      if (focus && focusRef.current) focusRef.current(focus);
    } else {
      setTurns((prev) => prev.map((t) => (t.client_turn_id === clientTurnId
        ? { ...t, pending: false, error: turnError(result) } : t)));
    }
    if (changedRef.current) changedRef.current();
  }, [transport, chatSurface]);

  const fail = useCallback((e) => {
    busyRef.current = false;
    if (!liveRef.current) return;
    setBusy(false);
    setProgress('');
    setLoadError(e.message);
  }, []);

  const send = useCallback((text) => {
    const question = text.trim();
    if (!question || busyRef.current || answering) return false;
    busyRef.current = true;
    run(question, newKey(), conversationId).catch(fail);
    return true;
  }, [conversationId, run, fail, answering]);

  // A failed turn is sent again with the SAME id: the server replays it if it
  // finished, or answers from its saved changes, never running a change twice.
  const retry = useCallback((turn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    run(turn.question, turn.client_turn_id, conversationId).catch(fail);
  }, [conversationId, run, fail]);

  const setProposalState = useCallback((id, value) => {
    setProposals((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...value } }));
  }, []);

  return { available, conversationId, turns, draft, progress, busy, loadError, send, retry,
    answering: answering && !busy, proposals, setProposalState };
}
